import Connection from "@/functions/Connection.js";

/**
 * Recurso REST exposto pelo gspro12-api para a tabela `configuracoes_sistema`.
 * O `Connection` ja aplica o baseURL (VUE_APP_URL), o Bearer token e devolve
 * `response.data`, entao os metodos abaixo resolvem direto no payload da API.
 *
 * Rota montada no grupo autenticado do `start/routes.js` do backend
 * (`Route.resource(...).apiOnly()` com prefixo `api` e middleware `auth`):
 *   GET    api/configuracao_sistema        -> index
 *   POST   api/configuracao_sistema        -> store
 *   GET    api/configuracao_sistema/:id    -> show
 *   PUT    api/configuracao_sistema/:id    -> update
 *   DELETE api/configuracao_sistema/:id    -> destroy
 */
const RESOURCE = "configuracao_sistema";

/**
 * Chaves cujo valor nunca deve ser exibido em claro na listagem.
 * Baseado no `ConfiguracaoSeeder` do backend (tokens, secrets e credenciais).
 */
const PADROES_SENSIVEIS = ["TOKEN", "SECRET", "KEY", "SENHA", "PASSWORD"];

const ConfiguracaoSistemaService = {
  /**
   * Lista todas as configuracoes.
   * @returns {Promise<Array<{id:number, chave:string, valor:string, descricao:string}>>}
   */
  async listar () {
    const res = await Connection.get(RESOURCE);
    return Array.isArray(res) ? res : [];
  },

  /**
   * Busca uma configuracao pelo id.
   */
  async buscar (id) {
    return await Connection.get(`${RESOURCE}/${id}`);
  },

  /**
   * Cria uma nova configuracao.
   * @param {{chave:string, valor:string, descricao:string}} configuracao
   */
  async cadastrar (configuracao) {
    return await Connection.post(RESOURCE, {
      chave: configuracao.chave,
      valor: configuracao.valor,
      descricao: configuracao.descricao
    });
  },

  /**
   * Atualiza uma configuracao existente.
   */
  async atualizar (id, configuracao) {
    return await Connection.put(`${RESOURCE}/${id}`, {
      chave: configuracao.chave,
      valor: configuracao.valor,
      descricao: configuracao.descricao
    });
  },

  /**
   * Remove uma configuracao.
   */
  async excluir (id) {
    return await Connection.delete(`${RESOURCE}/${id}`);
  },

  /**
   * Indica se o valor da chave deve ser mascarado na listagem.
   */
  ehSensivel (chave) {
    if (!chave) return false;
    const alvo = String(chave).toUpperCase();
    return PADROES_SENSIVEIS.some(padrao => alvo.includes(padrao));
  },

  /**
   * Prepara o valor para exibicao na tabela: mascara segredos e trunca
   * valores longos (URLs de webservice, templates, etc).
   */
  formatarValor (chave, valor, limite = 60) {
    if (valor === null || valor === undefined || valor === "") return "-";
    const texto = String(valor);
    if (this.ehSensivel(chave)) {
      const visivel = texto.slice(-4);
      return `${"•".repeat(Math.min(texto.length, 12))}${texto.length > 4 ? visivel : ""}`;
    }
    return texto.length > limite ? `${texto.slice(0, limite)}...` : texto;
  },

  /**
   * Agrupa a chave em uma categoria a partir do prefixo, usada no filtro da tela.
   * Espelha os blocos declarados no `ConfiguracaoSeeder` do backend.
   */
  categoriaDaChave (chave) {
    if (!chave) return "Outras";
    const alvo = String(chave).toUpperCase();
    if (alvo.startsWith("WATI") || alvo.startsWith("WHATSAPP") || alvo.startsWith("BOTCONVERSA")) {
      return "WhatsApp";
    }
    if (alvo.startsWith("SPACES")) return "Storage";
    if (alvo.endsWith("TEMPLATEID")) return "E-mail";
    if (alvo.startsWith("NFSE") || alvo.startsWith("REISTECH") || alvo.startsWith("DANFSE")) {
      return "Fiscal";
    }
    if (alvo.startsWith("BOLETO") || alvo.startsWith("PORTAL")) return "Financeiro";
    if (alvo.startsWith("FRANCHISING")) return "Franquia";
    if (alvo.includes("_API") || alvo.includes("_URL") || alvo.endsWith("URL")) return "Integracoes";
    return "Outras";
  }
};

export default ConfiguracaoSistemaService;
